/**
 * FormattingSpan - Character-level formatting for text runs
 *
 * This module defines the data structures for MS Word-style text formatting
 * where each character range can have independent styling (font, size, bold, etc.).
 *
 * Architecture:
 * - Document has default font settings
 * - Paragraphs can override document defaults
 * - FormattingSpans override paragraph defaults for specific character ranges
 *
 * Inheritance chain: document → paragraph → span
 *
 * @module FormattingSpan
 * @since 2026-01-21
 */

/**
 * Character-level formatting span (MS Word "text run" equivalent)
 *
 * Spans are stored per-paragraph and use grapheme indices relative to paragraph start.
 * Adjacent spans with identical formatting are automatically merged.
 * Characters not covered by any span inherit paragraph/document defaults.
 *
 * Example: "Hello World" with "World" bold
 * ```
 * text: "Hello World"
 * spans: [{ id: 'span-1', start: 6, end: 11, fontWeight: 700 }]
 * ```
 * Characters 0-5 ("Hello ") use paragraph/document defaults.
 */
export interface FormattingSpan {
  /** Unique span identifier (UUID for stable references across edits) */
  id: string;

  /** Start grapheme index within paragraph (inclusive, 0-based from paragraph start) */
  start: number;

  /** End grapheme index within paragraph (exclusive) */
  end: number;

  /**
   * Font UID override (e.g., "Inter-Bold.ttf")
   * undefined = inherit from paragraph/document
   */
  fontFamily?: string;

  /**
   * Font size in pixels override
   * undefined = inherit from paragraph/document
   */
  fontSizePx?: number;

  /**
   * Font weight override: 100-900
   * 400 = normal, 700 = bold
   * undefined = inherit from paragraph/document
   */
  fontWeight?: number;

  /**
   * Font style override
   * undefined = inherit from paragraph/document
   */
  fontStyle?: 'normal' | 'italic' | 'oblique';

  /**
   * Text color as CSS color string (e.g., "#000000", "rgb(0,0,0)")
   * undefined = inherit from paragraph/document
   */
  color?: string;

  /**
   * Background/highlight color
   * undefined = no highlight
   */
  backgroundColor?: string;

  /**
   * Underline style
   * undefined = no underline
   */
  underline?: 'none' | 'single' | 'double' | 'wavy';

  /**
   * Strikethrough
   * undefined = no strikethrough
   */
  strikethrough?: boolean;

  /**
   * Superscript (raised text, smaller size)
   * undefined = normal baseline
   */
  superscript?: boolean;

  /**
   * Subscript (lowered text, smaller size)
   * undefined = normal baseline
   */
  subscript?: boolean;

  /**
   * OpenType features to enable (e.g., ['smcp', 'liga', 'kern'])
   * undefined = use font defaults
   */
  features?: string[];
}

/**
 * Complete formatting state with all properties resolved
 * Used after inheritance resolution from document → paragraph → span
 *
 * All fields are required (no optionals) - this represents the final
 * computed style for rendering.
 */
export interface ResolvedFormatting {
  /** Resolved font UID (e.g., "Inter-Regular.ttf") */
  fontFamily: string;

  /** Resolved font size in pixels */
  fontSizePx: number;

  /** Resolved font weight (100-900) */
  fontWeight: number;

  /** Resolved font style */
  fontStyle: 'normal' | 'italic' | 'oblique';

  /** Resolved text color (CSS color string) */
  color: string;

  /** Resolved background color (null = transparent) */
  backgroundColor: string | null;

  /** Resolved underline style */
  underline: 'none' | 'single' | 'double' | 'wavy';

  /** Resolved strikethrough */
  strikethrough: boolean;

  /** Resolved superscript */
  superscript: boolean;

  /** Resolved subscript */
  subscript: boolean;

  /** Resolved OpenType features */
  features: string[];
}

/**
 * Default formatting values (document-level defaults)
 * Used when no paragraph or span overrides are specified.
 */
export const DEFAULT_FORMATTING: ResolvedFormatting = {
  fontFamily: 'Inter-Regular.ttf',
  fontSizePx: 14,
  fontWeight: 400,
  fontStyle: 'normal',
  color: '#000000',
  backgroundColor: null,
  underline: 'none',
  strikethrough: false,
  superscript: false,
  subscript: false,
  features: []
};

/**
 * Formatting properties that can be applied to a selection
 * This is the subset of FormattingSpan that excludes id/start/end
 */
export type FormattingAttributes = Omit<FormattingSpan, 'id' | 'start' | 'end'>;

/**
 * Check if a FormattingSpan has any formatting properties set
 * (beyond just id, start, end)
 */
export function spanHasFormatting(span: FormattingSpan): boolean {
  return (
    span.fontFamily !== undefined ||
    span.fontSizePx !== undefined ||
    span.fontWeight !== undefined ||
    span.fontStyle !== undefined ||
    span.color !== undefined ||
    span.backgroundColor !== undefined ||
    span.underline !== undefined ||
    span.strikethrough !== undefined ||
    span.superscript !== undefined ||
    span.subscript !== undefined ||
    (span.features !== undefined && span.features.length > 0)
  );
}

/**
 * Get a list of formatting property keys that are set on a span
 */
export function getSetFormattingKeys(span: FormattingSpan): (keyof FormattingAttributes)[] {
  const keys: (keyof FormattingAttributes)[] = [];

  if (span.fontFamily !== undefined) keys.push('fontFamily');
  if (span.fontSizePx !== undefined) keys.push('fontSizePx');
  if (span.fontWeight !== undefined) keys.push('fontWeight');
  if (span.fontStyle !== undefined) keys.push('fontStyle');
  if (span.color !== undefined) keys.push('color');
  if (span.backgroundColor !== undefined) keys.push('backgroundColor');
  if (span.underline !== undefined) keys.push('underline');
  if (span.strikethrough !== undefined) keys.push('strikethrough');
  if (span.superscript !== undefined) keys.push('superscript');
  if (span.subscript !== undefined) keys.push('subscript');
  if (span.features !== undefined && span.features.length > 0) keys.push('features');

  return keys;
}

/**
 * Compare two spans for formatting equality (ignoring id, start, end)
 */
export function spansHaveEqualFormatting(a: FormattingSpan, b: FormattingSpan): boolean {
  if (a.fontFamily !== b.fontFamily) return false;
  if (a.fontSizePx !== b.fontSizePx) return false;
  if (a.fontWeight !== b.fontWeight) return false;
  if (a.fontStyle !== b.fontStyle) return false;
  if (a.color !== b.color) return false;
  if (a.backgroundColor !== b.backgroundColor) return false;
  if (a.underline !== b.underline) return false;
  if (a.strikethrough !== b.strikethrough) return false;
  if (a.superscript !== b.superscript) return false;
  if (a.subscript !== b.subscript) return false;

  // Compare features arrays
  const aFeatures = a.features || [];
  const bFeatures = b.features || [];
  if (aFeatures.length !== bFeatures.length) return false;
  for (let i = 0; i < aFeatures.length; i++) {
    if (aFeatures[i] !== bFeatures[i]) return false;
  }

  return true;
}
